import { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { buildUrl } from "api/base";
import { tableStyles } from "utils/tableStyles";
import DataTable from "../../examples/Tables/DataTable";

// ============================================================================
// User Management Dialogs
// ============================================================================

function AddUserDialog({ open, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    id_pengguna: "",
    katalaluan: "",
    menu: "BAHAGIAN",
    bahagian: "",
    emel_pengguna: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
    if (error) setError("");
  };

  const handleSubmit = async () => {
    if (!formData.id_pengguna || !formData.katalaluan || !formData.bahagian || !formData.emel_pengguna) {
      setError("Semua medan diperlukan");
      return;
    }

    setLoading(true);
    setError("");
    
    try {
      const res = await fetch(buildUrl("/admin/users"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Gagal menambah pengguna");
      }

      onSuccess();
      onClose();
      setFormData({
        id_pengguna: "",
        katalaluan: "",
        menu: "BAHAGIAN",
        bahagian: "",
        emel_pengguna: "",
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Tambah Pengguna Baru</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <TextField
          label="ID Pengguna"
          fullWidth
          margin="normal"
          value={formData.id_pengguna}
          onChange={handleChange("id_pengguna")}
        />
        
        <TextField
          label="Kata Laluan"
          type="password"
          fullWidth
          margin="normal"
          value={formData.katalaluan}
          onChange={handleChange("katalaluan")}
        />
        
        <FormControl fullWidth margin="normal">
          <InputLabel>Menu Type</InputLabel>
          <Select value={formData.menu} onChange={handleChange("menu")}>
            <MenuItem value="ADMIN">ADMIN</MenuItem>
            <MenuItem value="KAUNTER">KAUNTER</MenuItem>
            <MenuItem value="BAHAGIAN">BAHAGIAN</MenuItem>
          </Select>
        </FormControl>
        
        <TextField
          label="Bahagian"
          fullWidth
          margin="normal"
          value={formData.bahagian}
          onChange={handleChange("bahagian")}
          placeholder="e.g., UEU, HT, PBD"
        />
        
        <TextField
          label="Emel"
          type="email"
          fullWidth
          margin="normal"
          value={formData.emel_pengguna}
          onChange={handleChange("emel_pengguna")}
        />
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} color="secondary">Batal</MDButton>
        <MDButton onClick={handleSubmit} color="primary" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : "Tambah"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

AddUserDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

function EditUserDialog({ open, onClose, onSuccess, user }) {
  const [formData, setFormData] = useState({
    menu: "",
    bahagian: "",
    emel_pengguna: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) {
      setFormData({
        menu: user.menu || "BAHAGIAN",
        bahagian: user.bahagian || "",
        emel_pengguna: user.emel_pengguna || "",
      });
    }
  }, [user]);

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
    if (error) setError("");
  };

  const handleSubmit = async () => {
    if (!formData.bahagian || !formData.emel_pengguna) {
      setError("Semua medan diperlukan");
      return;
    }

    setLoading(true);
    setError("");
    
    try {
      const res = await fetch(buildUrl(`/admin/users/${user.id}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Gagal mengemaskini pengguna");
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Pengguna: {user?.id_pengguna}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <FormControl fullWidth margin="normal">
          <InputLabel>Menu Type</InputLabel>
          <Select value={formData.menu} onChange={handleChange("menu")}>
            <MenuItem value="ADMIN">ADMIN</MenuItem>
            <MenuItem value="KAUNTER">KAUNTER</MenuItem>
            <MenuItem value="BAHAGIAN">BAHAGIAN</MenuItem>
          </Select>
        </FormControl>
        
        <TextField
          label="Bahagian"
          fullWidth
          margin="normal"
          value={formData.bahagian}
          onChange={handleChange("bahagian")}
        />
        
        <TextField
          label="Emel"
          type="email"
          fullWidth
          margin="normal"
          value={formData.emel_pengguna}
          onChange={handleChange("emel_pengguna")}
        />
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} color="secondary">Batal</MDButton>
        <MDButton onClick={handleSubmit} color="primary" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : "Simpan"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

EditUserDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.number,
    id_pengguna: PropTypes.string,
    menu: PropTypes.string,
    bahagian: PropTypes.string,
    emel_pengguna: PropTypes.string,
  }),
};

function ResetPasswordDialog({ open, onClose, onSuccess, user }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    if (!password || password.length < 6) {
      setError("Kata laluan mesti sekurang-kurangnya 6 aksara");
      return;
    }

    setLoading(true);
    setError("");
    
    try {
      const res = await fetch(buildUrl(`/admin/users/${user.id}/password`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ katalaluan: password }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Gagal mengemas kini kata laluan");
      }

      onSuccess();
      onClose();
      setPassword("");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reset Kata Laluan: {user?.id_pengguna}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <TextField
          label="Kata Laluan Baru"
          type="password"
          fullWidth
          margin="normal"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (error) setError("");
          }}
          helperText="Minimum 6 aksara"
        />
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} color="secondary">Batal</MDButton>
        <MDButton onClick={handleSubmit} color="warning" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : "Reset"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

ResetPasswordDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.number,
    id_pengguna: PropTypes.string,
  }),
};

// ============================================================================
// Tabs
// ============================================================================

function UsersTab() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [menuFilter, setMenuFilter] = useState("");
  
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [resetPwDialogOpen, setResetPwDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await fetch(buildUrl("/admin/users"), { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.users || []);
    } catch (e) {
      setError(e.message || "Ralat memuat pengguna");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditDialogOpen(true);
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setResetPwDialogOpen(true);
  };

  const handleSuccess = () => {
    loadUsers();
  };

  // Get unique departments and menus for filters
  const departments = useMemo(() => [...new Set(rows.map(r => r.bahagian))].sort(), [rows]);
  const menuTypes = useMemo(() => [...new Set(rows.map(r => r.menu))].sort(), [rows]);

  // Filter rows
  const filteredRows = useMemo(() => {
    return rows.filter(row => {
      const matchesSearch = !searchTerm || 
        row.id_pengguna.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.emel_pengguna.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDept = !deptFilter || row.bahagian === deptFilter;
      const matchesMenu = !menuFilter || row.menu === menuFilter;
      return matchesSearch && matchesDept && matchesMenu;
    });
  }, [rows, searchTerm, deptFilter, menuFilter]);

  const columns = useMemo(() => [
    { Header: "ID Pengguna", accessor: "id_pengguna", width: "20%", align: "left" },
    { Header: "Menu", accessor: "menu", width: "12%", align: "center" },
    { Header: "Bahagian", accessor: "bahagian", width: "15%", align: "left" },
    { Header: "Emel", accessor: "emel_pengguna", width: "25%", align: "left" },
    { Header: "Tindakan", accessor: "actions", width: "18%", align: "center" },
  ], []);

  const tableRows = useMemo(() => filteredRows.map((u) => ({
    id: u.id,
    id_pengguna: u.id_pengguna,
    menu: (
      <Chip 
        label={u.menu} 
        size="small"
        color={u.menu === "ADMIN" ? "error" : u.menu === "KAUNTER" ? "warning" : "info"}
      />
    ),
    bahagian: u.bahagian,
    emel_pengguna: u.emel_pengguna,
    actions: (
      <MDBox display="flex" gap={1} justifyContent="center">
        <IconButton size="small" color="info" onClick={() => handleEdit(u)}>
          <Icon fontSize="small">edit</Icon>
        </IconButton>
        <IconButton size="small" color="warning" onClick={() => handleResetPassword(u)}>
          <Icon fontSize="small">lock_reset</Icon>
        </IconButton>
      </MDBox>
    ),
  })), [filteredRows]);

  if (loading) return <MDBox p={1}><CircularProgress size={20} /></MDBox>;
  if (error) return <MDBox p={1}><MDTypography variant="caption" color="error">{error}</MDTypography></MDBox>;

  return (
    <MDBox p={1}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <MDTypography variant="caption" fontWeight="bold">
          Pengguna ({filteredRows.length}/{rows.length})
        </MDTypography>
        <MDButton color="success" size="small" onClick={() => setAddDialogOpen(true)} sx={{ py: 0.3, fontSize: "0.7rem" }}>
          <Icon sx={{ fontSize: "0.9rem" }}>add</Icon>&nbsp;Tambah
        </MDButton>
      </MDBox>

      <MDBox display="flex" gap={1} mb={1} flexWrap="wrap">
        <MDInput
          size="small"
          placeholder="Cari..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: 150, "& input": { fontSize: "0.75rem", py: 0.5 } }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={deptFilter}
            onChange={(e) => setDeptFilter(e.target.value)}
            displayEmpty
            sx={{ fontSize: "0.75rem", py: 0 }}
          >
            <MenuItem value="" sx={{ fontSize: "0.75rem" }}>Semua Dept</MenuItem>
            {departments.map(d => <MenuItem key={d} value={d} sx={{ fontSize: "0.75rem" }}>{d}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={menuFilter}
            onChange={(e) => setMenuFilter(e.target.value)}
            displayEmpty
            sx={{ fontSize: "0.75rem", py: 0 }}
          >
            <MenuItem value="" sx={{ fontSize: "0.75rem" }}>Semua Menu</MenuItem>
            {menuTypes.map(m => <MenuItem key={m} value={m} sx={{ fontSize: "0.75rem" }}>{m}</MenuItem>)}
          </Select>
        </FormControl>
      </MDBox>

      <DataTable
        table={{ columns, rows: tableRows }}
        entriesPerPage={{ defaultValue: 10 }}
        canSearch={false}
        showTotalEntries={false}
        isSorted={false}
        fontSize={"0.75rem"}
      />

      <AddUserDialog 
        open={addDialogOpen} 
        onClose={() => setAddDialogOpen(false)}
        onSuccess={handleSuccess}
      />
      <EditUserDialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        onSuccess={handleSuccess}
        user={selectedUser}
      />
      <ResetPasswordDialog 
        open={resetPwDialogOpen} 
        onClose={() => setResetPwDialogOpen(false)}
        onSuccess={handleSuccess}
        user={selectedUser}
      />
    </MDBox>
  );
}

function DepartmentsTab() {
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(buildUrl("/admin/departments/summary"), { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (active) setDepartments(data.departments || []);
      } catch (e) {
        if (active) setError(e.message || "Ralat memuat bahagian");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  if (loading) return <MDBox p={1}><CircularProgress size={20} /></MDBox>;
  if (error) return <MDBox p={1}><MDTypography variant="caption" color="error">{error}</MDTypography></MDBox>;

  const healthyDepts = departments.filter(d => d.status === 'healthy');
  const warningDepts = departments.filter(d => d.status === 'warning');
  const criticalDepts = departments.filter(d => d.status === 'critical');

  return (
    <MDBox p={1}>
      <MDBox display="flex" gap={1} mb={1}>
        <Card sx={{ flex: 1, p: 1, bgcolor: "success.main", color: "white", textAlign: "center" }}>
          <MDTypography variant="caption" color="white" sx={{ fontSize: "0.7rem" }}>Sihat</MDTypography>
          <MDTypography variant="h5" color="white">{healthyDepts.length}</MDTypography>
        </Card>
        <Card sx={{ flex: 1, p: 1, bgcolor: "warning.main", color: "white", textAlign: "center" }}>
          <MDTypography variant="caption" color="white" sx={{ fontSize: "0.7rem" }}>Amaran</MDTypography>
          <MDTypography variant="h5" color="white">{warningDepts.length}</MDTypography>
        </Card>
        <Card sx={{ flex: 1, p: 1, bgcolor: "error.main", color: "white", textAlign: "center" }}>
          <MDTypography variant="caption" color="white" sx={{ fontSize: "0.7rem" }}>Kritikal</MDTypography>
          <MDTypography variant="h5" color="white">{criticalDepts.length}</MDTypography>
        </Card>
      </MDBox>

      <TableContainer component={Paper} sx={{ ...tableStyles.dense, maxHeight: 380 }}>
        <Table size="small" stickyHeader>
            <TableRow>
              <TableCell>Dept</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">User</TableCell>
              <TableCell align="center">Harta</TableCell>
              <TableCell align="center">Akaun</TableCell>
              <TableCell align="center">Inbois</TableCell>
              <TableCell align="center">Unpaid</TableCell>
            </TableRow>
          <TableBody>
            {departments.map((dept) => (
              <TableRow key={dept.code} hover>
                <TableCell sx={{ fontWeight: "bold" }}>
                  {dept.code}
                  {dept.issues && dept.issues.length > 0 && (
                    <MDBox display="inline" ml={0.5}>
                      <Chip label={dept.issues.length} size="small" color="warning" sx={{ height: 14, fontSize: "0.6rem" }} />
                    </MDBox>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Chip 
                    label={dept.health_score} 
                    size="small"
                    sx={{ height: 18, fontSize: "0.65rem" }}
                    color={dept.status === 'healthy' ? 'success' : dept.status === 'warning' ? 'warning' : 'error'}
                  />
                </TableCell>
                <TableCell align="center">{dept.user_count}</TableCell>
                <TableCell align="center">{dept.property_count}</TableCell>
                <TableCell align="center">{dept.account_count}</TableCell>
                <TableCell align="center">{dept.invoice_count}</TableCell>
                <TableCell align="center">
                  <Chip label={dept.unpaid_invoice_count} size="small" sx={{ height: 16, fontSize: "0.65rem" }}
                    color={dept.unpaid_invoice_count > 0 ? "error" : "default"} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </MDBox>
  );
}

function SystemHealthTab() {
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(buildUrl("/admin/system/health"), { credentials: "include" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (active) setHealth(data);
      } catch (e) {
        if (active) setError(e.message || "Ralat memuat status sistem");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  if (loading) return <MDBox p={1}><CircularProgress size={20} /></MDBox>;
  if (error) return <MDBox p={1}><MDTypography variant="caption" color="error">{error}</MDTypography></MDBox>;
  if (!health) return null;

  return (
    <MDBox p={1}>
      <Grid container spacing={1}>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 1.5, textAlign: "center" }}>
            <MDTypography variant="caption" sx={{ fontSize: "0.7rem" }}>Skor Kesihatan</MDTypography>
            <MDTypography variant="h2" color={health.status === 'healthy' ? 'success' : health.status === 'warning' ? 'warning' : 'error'}>
              {health.health_score}
            </MDTypography>
            <Chip 
              label={health.status} 
              size="small"
              sx={{ height: 18, fontSize: "0.65rem" }}
              color={health.status === 'healthy' ? 'success' : health.status === 'warning' ? 'warning' : 'error'}
            />
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ p: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={3}>
                <MDTypography variant="caption" sx={{ fontSize: "0.65rem" }}>Users</MDTypography>
                <MDTypography variant="h6">{health.stats.total_users}</MDTypography>
              </Grid>
              <Grid item xs={3}>
                <MDTypography variant="caption" sx={{ fontSize: "0.65rem" }}>Depts</MDTypography>
                <MDTypography variant="h6">{health.stats.total_departments}</MDTypography>
              </Grid>
              <Grid item xs={3}>
                <MDTypography variant="caption" sx={{ fontSize: "0.65rem" }}>Harta</MDTypography>
                <MDTypography variant="h6">{health.stats.total_properties}</MDTypography>
              </Grid>
              <Grid item xs={3}>
                <MDTypography variant="caption" sx={{ fontSize: "0.65rem" }}>Akaun</MDTypography>
                <MDTypography variant="h6">{health.stats.total_accounts}</MDTypography>
              </Grid>
              <Grid item xs={4}>
                <MDTypography variant="caption" sx={{ fontSize: "0.65rem" }}>Inbois (bulan)</MDTypography>
                <MDTypography variant="h6">{health.stats.invoices_this_month}</MDTypography>
              </Grid>
              <Grid item xs={4}>
                <MDTypography variant="caption" sx={{ fontSize: "0.65rem" }}>Bayaran (bulan)</MDTypography>
                <MDTypography variant="h6">{health.stats.payments_this_month}</MDTypography>
              </Grid>
              <Grid item xs={4}>
                <MDTypography variant="caption" sx={{ fontSize: "0.65rem" }}>Unpaid</MDTypography>
                <MDTypography variant="h6" color="error">{health.stats.pending_invoices}</MDTypography>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {health.issues && health.issues.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ p: 1 }}>
              <MDTypography variant="caption" fontWeight="bold" sx={{ fontSize: "0.7rem" }}>
                ⚠️ Issues ({health.issues.length})
              </MDTypography>
              {health.issues.map((issue, i) => (
                <Alert key={i} severity={issue.severity} sx={{ my: 0.5, py: 0.5 }}>
                  <MDTypography variant="caption" sx={{ fontSize: "0.7rem" }}>{issue.message}</MDTypography>
                  {issue.departments && issue.departments.length > 0 && (
                    <MDBox mt={0.5}>
                      {issue.departments.map((dept, j) => (
                        <Chip key={j} label={dept} size="small" sx={{ mr: 0.3, height: 16, fontSize: "0.6rem" }} />
                      ))}
                    </MDBox>
                  )}
                </Alert>
              ))}
            </Card>
          </Grid>
        )}
      </Grid>
    </MDBox>
  );
}

function AuditTab() {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [deptFilter, setDeptFilter] = useState("");

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "100");
      if (userFilter) params.set("user", userFilter);
      if (deptFilter) params.set("department", deptFilter);
      
      const res = await fetch(buildUrl(`/admin/logs/audit?${params}`), { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      
      // Parse log lines into structured data
      const parsed = (data.lines || []).map((line, idx) => {
        try {
          const obj = JSON.parse(line);
          return { ...obj, _raw: line, _id: idx };
        } catch {
          return { _raw: line, _id: idx, ts: '', method: '', path: '', user: '', bahagian: '' };
        }
      });
      
      setLogs(parsed);
    } catch (e) {
      setError(e.message || "Ralat memuat log audit");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, [userFilter, deptFilter]);

  if (loading) return <MDBox p={1}><CircularProgress size={20} /></MDBox>;
  if (error) return <MDBox p={1}><MDTypography variant="caption" color="error">{error}</MDTypography></MDBox>;

  return (
    <MDBox p={1}>
      <MDBox display="flex" gap={1} mb={1} alignItems="center">
        <MDTypography variant="caption" fontWeight="bold">Audit ({logs.length})</MDTypography>
        <MDInput
          size="small"
          placeholder="User"
          value={userFilter}
          onChange={(e) => setUserFilter(e.target.value)}
          sx={{ width: 120, "& input": { fontSize: "0.75rem", py: 0.5 } }}
        />
        <MDInput
          size="small"
          placeholder="Dept"
          value={deptFilter}
          onChange={(e) => setDeptFilter(e.target.value)}
          sx={{ width: 100, "& input": { fontSize: "0.75rem", py: 0.5 } }}
        />
      </MDBox>

      <TableContainer component={Paper} sx={{ ...tableStyles.dense, maxHeight: 380 }}>
        <Table size="small" stickyHeader>
            <TableRow>
              <TableCell>Masa</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Path</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Dept</TableCell>
            </TableRow>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log._id} hover>
                <TableCell>
                  {log.ts ? new Date(log.ts).toLocaleTimeString('en-MY') : '-'}
                </TableCell>
                <TableCell>
                  <Chip label={log.method || '-'} size="small" sx={{ height: 16, fontSize: "0.6rem" }} 
                    color={log.method === 'POST' ? 'success' : log.method === 'PUT' ? 'warning' : 'default'} />
                </TableCell>
                <TableCell sx={{ maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis" }}>
                  {log.path || '-'}
                </TableCell>
                <TableCell>
                  {log.user?.id_pengguna || '-'}
                </TableCell>
                <TableCell>
                  {log.user?.bahagian || log.bahagian || '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </MDBox>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function AdminHome() {
  const [tab, setTab] = useState(0);
  const handleChange = (_e, v) => setTab(v);
  
  const tabs = [
    { label: "Dashboard", icon: "dashboard", component: SystemHealthTab },
    { label: "Pengguna", icon: "people", component: UsersTab },
    { label: "Bahagian", icon: "business", component: DepartmentsTab },
    { label: "Audit", icon: "history", component: AuditTab },
  ];

  const View = tabs[tab].component;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox p={1}>
        <Card>
          <MDBox px={2} pt={1.5} pb={0.5}>
            <MDTypography variant="h6" sx={{ fontSize: "1rem" }}>Admin Panel</MDTypography>
            <MDTypography variant="caption" color="text">Pengurusan Sistem</MDTypography>
          </MDBox>
          <Tabs value={tab} onChange={handleChange} variant="scrollable" allowScrollButtonsMobile sx={{ minHeight: 40 }}>
            {tabs.map((t, i) => (
              <Tab 
                key={i} 
                label={t.label} 
                icon={<Icon sx={{ fontSize: "1rem" }}>{t.icon}</Icon>}
                iconPosition="start"
                sx={{ minHeight: 40, py: 0.5, fontSize: "0.8rem" }}
              />
            ))}
          </Tabs>
          <View />
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}
